import {
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Form,
  FormButton,
  FormField,
  OptionType,
} from 'platform/components';
import {Grid, Heading, Hide, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {UseFormReturn} from 'react-hook-form';

import {
  AftersalesPaymentMethod,
  Payment,
  usePostCheckoutPayBankTransferMutation,
  usePostCheckoutPayCardMutation,
  usePostCheckoutPayCardReceiptMutation,
  usePostCheckoutPayCashMutation,
  usePostCheckoutPayCashReceiptMutation,
  usePostCheckoutRecordPaidMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError, useBank, useCashRegister} from '@dms/shared';

import {
  buildArray,
  CurrencyCodeType,
  getApiDateString,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

interface PayDialogProps extends RequiredTestIdProps {
  checkoutId: string;
  payment: Payment;
  onClose: () => void;
}

export function PayDialog(props: PayDialogProps) {
  const {getBankAccountByNumber, bankAccountOptions} = useBank();
  const {cashRegisters} = useCashRegister();
  const formatCurrency = useFormatCurrency();

  const [postCheckoutRecordPaid] = usePostCheckoutRecordPaidMutation();
  const [postCheckoutPayBankTransfer] = usePostCheckoutPayBankTransferMutation();
  const [postCheckoutPayCard] = usePostCheckoutPayCardMutation();
  const [postCheckoutPayCardReceipt] = usePostCheckoutPayCardReceiptMutation();
  const [postCheckoutPayCash] = usePostCheckoutPayCashMutation();
  const [postCheckoutPayCashReceipt] = usePostCheckoutPayCashReceiptMutation();

  const paymentAmount = props.payment.foreignCurrencyPaymentAmount ?? props.payment.paymentAmount;
  const remainingPaymentAmount =
    props.payment.remainingForeignCurrencyPaymentAmount ?? props.payment.remainingPaymentAmount;

  const handlePayPayment = (data: PayFormType) => {
    const bankAccount = data.tenantBankAccount
      ? getBankAccountByNumber(data.tenantBankAccount!)
      : undefined;

    const request = match([data.paymentMethod, data.withCashRegisterDocument])
      .with(['BANK_TRANSFER', Pattern.boolean], () =>
        postCheckoutPayBankTransfer({
          checkoutId: props.checkoutId,
          paymentId: props.payment.checkoutPaymentId,
          body: {
            amount: data.amount,
            currency: paymentAmount.currency,
            payDate: getApiDateString(data.payDate),
            withTaxDocument: data.withTaxDocument,
            bankAccount: bankAccount
              ? {
                  currency: bankAccount.currency ?? paymentAmount.currency,
                  isPrimary: !!bankAccount.isPrimary,
                  number: bankAccount.accountNumber,
                  name: bankAccount.accountName,
                  bankName: bankAccount.bankName,
                  iban: bankAccount.iban,
                  swift: bankAccount.swift,
                }
              : undefined,
          },
        })
      )
      .with(['CARD', false], () =>
        postCheckoutPayCard({
          checkoutId: props.checkoutId,
          paymentId: props.payment.checkoutPaymentId,
          body: {
            amount: data.amount,
            currency: paymentAmount.currency,
            payDate: getApiDateString(data.payDate),
            withTaxDocument: data.withTaxDocument,
          },
        })
      )
      .with(['CASH', false], () =>
        postCheckoutPayCash({
          checkoutId: props.checkoutId,
          paymentId: props.payment.checkoutPaymentId,
          body: {
            amount: data.amount,
            currency: paymentAmount.currency,
            payDate: getApiDateString(data.payDate),
            withTaxDocument: data.withTaxDocument,
            cashRegisterId: data.cashRegisterId!,
          },
        })
      )
      .with(['CARD', true], () =>
        postCheckoutPayCardReceipt({
          checkoutId: props.checkoutId,
          paymentId: props.payment.checkoutPaymentId,
          body: {
            amount: data.amount,
            currency: paymentAmount.currency,
            payDate: getApiDateString(data.payDate),
            withTaxDocument: data.withTaxDocument,
            cashRegisterId: data.cashRegisterId!,
          },
        })
      )
      .with(['CASH', true], () =>
        postCheckoutPayCashReceipt({
          checkoutId: props.checkoutId,
          paymentId: props.payment.checkoutPaymentId,
          body: {
            amount: data.amount,
            currency: paymentAmount.currency,
            payDate: getApiDateString(data.payDate),
            withTaxDocument: data.withTaxDocument,
            cashRegisterId: data.cashRegisterId!,
          },
        })
      )
      .with(['INTERNAL', Pattern.boolean], () =>
        postCheckoutRecordPaid({
          checkoutId: props.checkoutId,
          paymentId: props.payment.checkoutPaymentId,
          body: {
            amount: data.amount,
            currency: paymentAmount.currency,
            payDate: getApiDateString(data.payDate),
            withCashRegisterDocument: data.cashRegisterId ? data.withCashRegisterDocument : false,
            cashRegisterId: data.cashRegisterId ?? undefined,
            paymentMethod: data.paymentMethod,

            tenantBankAccount: bankAccount
              ? {
                  currencyCode: bankAccount.currency ?? paymentAmount.currency,
                  isPrimary: !!bankAccount.isPrimary,
                  iban: bankAccount.iban,
                  name: bankAccount.bankName,
                  accountName: bankAccount.accountName ?? '',
                  accountNumber: bankAccount.accountNumber ?? '',
                  swift: bankAccount.swift,
                }
              : undefined,
          },
        })
      )
      .exhaustive();

    return request.unwrap().then(props.onClose).catch(handleApiError);
  };

  const currencyData = paymentAmount.currency as CurrencyCodeType;

  const orderPaymentOptions = buildArray<OptionType<AftersalesPaymentMethod>>()
    .when(props.payment.availablePaymentMethods?.includes('BANK_TRANSFER'), {
      label: i18n.t('entity.invoice.paymentMethod.bankTransfer'),
      value: 'BANK_TRANSFER',
    })
    .when(props.payment.availablePaymentMethods?.includes('CARD'), {
      label: i18n.t('entity.invoice.paymentMethod.card'),
      value: 'CARD',
    })
    .when(props.payment.availablePaymentMethods?.includes('CASH'), {
      label: i18n.t('entity.invoice.paymentMethod.cash'),
      value: 'CASH',
    })
    .when(props.payment.availablePaymentMethods?.includes('INTERNAL'), {
      label: i18n.t('general.labels.internal'),
      value: 'INTERNAL',
    });

  const handlePaymentMethodChange = (
    formApi: UseFormReturn<PayFormType>,
    value: AftersalesPaymentMethod | Nullish
  ) => {
    formApi.resetField('cashRegisterId');
    formApi.setValue('withCashRegisterDocument', value === 'CASH');
  };

  const getCashRegisterOptions = (paymentType: AftersalesPaymentMethod) =>
    cashRegisters
      .filter((cashRegister) => cashRegister.eligibleForCardPayments || paymentType === 'CASH')
      .map((cashRegister) => ({
        value: cashRegister.id,
        label: cashRegister.name,
      }));

  return (
    <VStack spacing={4}>
      <HStack spacing={20}>
        <VStack spacing={1}>
          <Text size="xSmall" color="secondary">
            {i18n.t('general.labels.amount')}
          </Text>
          <Heading size={4} data-testid={props['data-testid']}>
            {formatCurrency(paymentAmount.amount, paymentAmount.currency, 2)}
          </Heading>
        </VStack>
        <VStack spacing={1}>
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.invoice.labels.remaining')}
          </Text>
          <Heading size={4} data-testid={props['data-testid']} color="danger">
            {formatCurrency(remainingPaymentAmount.amount, remainingPaymentAmount.currency, 2)}
          </Heading>
        </VStack>
      </HStack>
      <Form<PayFormType>
        defaultValues={{
          amount: remainingPaymentAmount.amount,
          payDate: new Date(),
          cashRegisterId: props.payment.cashRegisterId ?? undefined,
          paymentMethod: props.payment.method,
          tenantBankAccount: props.payment.bankAccount?.number ?? undefined,
          withCashRegisterDocument: props.payment.method === 'CASH',
          withTaxDocument: props.payment.discriminator === 'DEPOSIT' ? true : false,
        }}
        experimentalZodSchema={formSchema}
        onSubmit={handlePayPayment}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <Card variant="inlineGrey">
              <Grid columns={2}>
                <FormField
                  control={control}
                  name="amount"
                  type="currency"
                  currency={currencyData}
                  label={i18n.t('general.labels.amount')}
                  isRequired
                  data-testid={suffixTestId('amount', props)}
                />
                <FormField
                  control={control}
                  name="payDate"
                  type="date"
                  isRelativeDatesHidden
                  label={
                    props.payment.discriminator === 'DEPOSIT'
                      ? i18n.t('entity.invoice.labels.dateOfPaymentWithDUZP')
                      : i18n.t('entity.invoice.labels.dateOfPayment')
                  }
                  data-testid={suffixTestId('payDate', props)}
                  isRequired
                />
                <FormField
                  control={control}
                  name="paymentMethod"
                  type="choice"
                  label={i18n.t('entity.checkout.labels.paymentMethod')}
                  options={orderPaymentOptions}
                  isNotClearable
                  isRequired
                  menuInPortal
                  onChange={(value) =>
                    handlePaymentMethodChange(formApi, value as AftersalesPaymentMethod)
                  }
                  data-testid={suffixTestId('paymentMethod', props)}
                />
                <Show when={formApi.watch('paymentMethod') === 'BANK_TRANSFER'}>
                  <FormField
                    control={control}
                    name="tenantBankAccount"
                    type="choice"
                    options={bankAccountOptions}
                    label={i18n.t('entity.checkout.labels.incomingBankAccount')}
                    isRequired
                    isNotClearable
                    menuInPortal
                    data-testid={suffixTestId('incomingBankAccount', props)}
                  />
                </Show>
                <Show
                  when={
                    formApi.watch('paymentMethod') === 'CASH' ||
                    formApi.watch('paymentMethod') === 'CARD'
                  }
                >
                  <FormField
                    control={control}
                    name="cashRegisterId"
                    type="choice"
                    options={getCashRegisterOptions(formApi.watch('paymentMethod'))}
                    label={i18n.t('entity.checkout.labels.cashRegister')}
                    isNotClearable
                    menuInPortal
                    data-testid={suffixTestId('incomingBankAccount', props)}
                  />
                  <Hide when={getCashRegisterOptions(formApi.watch('paymentMethod')).length === 0}>
                    <FormField
                      control={control}
                      name="withCashRegisterDocument"
                      type="checkbox"
                      label={i18n.t('entity.cashRegister.labels.createCashReceipt')}
                      isDisabled={formApi.watch('paymentMethod') === 'CASH'}
                    />
                    <Space />
                  </Hide>
                  <Show when={props.payment.discriminator === 'DEPOSIT'}>
                    <FormField
                      control={control}
                      name="withTaxDocument"
                      type="checkbox"
                      label={i18n.t('entity.cashRegister.labels.shouldCreateTaxDocumentForPayment')}
                    />
                  </Show>
                  <Show when={getCashRegisterOptions(formApi.watch('paymentMethod')).length === 0}>
                    <Checkbox
                      label={i18n.t('entity.cashRegister.labels.createCashReceipt')}
                      isDisabled
                      value={false}
                    />
                  </Show>
                </Show>
              </Grid>
            </Card>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                onClick={props.onClose}
                variant="secondary"
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.confirm')}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </VStack>
  );
}

type PayFormType = {
  payDate: Date;
  amount: number;
  paymentMethod: AftersalesPaymentMethod;
  cashRegisterId?: string | null;
  withCashRegisterDocument: boolean;
  tenantBankAccount?: string | null;
  withTaxDocument: boolean;
};

const formSchema = z
  .object({
    amount: z.number().positive(),
    paymentMethod: z.enum(['BANK_TRANSFER', 'CASH', 'CARD', 'INTERNAL']),
    payDate: z.coerce.date(),
    tenantBankAccount: z.string().optional().nullable(),
    cashRegisterId: z.string().optional().nullable(),
    withCashRegisterDocument: z.boolean(),
    withTaxDocument: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.paymentMethod === 'BANK_TRANSFER') {
        return data.tenantBankAccount != null && data.tenantBankAccount !== '';
      }
      return true;
    },
    {
      message: i18n.t('entity.cashRegister.labels.bankAccountRequired'),
      path: ['tenantBankAccount'],
    }
  )
  .refine(
    (data) => {
      if (data.paymentMethod === 'CASH') {
        return data.cashRegisterId != null && data.cashRegisterId !== '';
      }
      return true;
    },
    {
      message: i18n.t('entity.cashRegister.labels.cashRegisterRequiredCash'),
      path: ['cashRegisterId'],
    }
  )
  .refine(
    (data) => {
      if (data.withCashRegisterDocument) {
        return data.cashRegisterId != null && data.cashRegisterId !== '';
      }
      return true;
    },
    {
      message: i18n.t('entity.cashRegister.labels.cashRegisterRequiredCashReceipt'),
      path: ['cashRegisterId'],
    }
  );
